import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ENUM_LABELS, type OrderRequestRow } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { useMutation } from '#client/hooks/useMutation';
import { RESOURCE, routes } from '#client/lib/routes';
import {
  type DeclineState,
  canDeleteRequest,
  canEditRequest,
  hasRequestActions,
  summariseLines,
} from './order-request.logic';
import {
  DeclineForm,
  RequestActions,
  RequestDetails,
  RequestStatusBanners,
  RequestedTestsCard,
} from './OrderRequestDetailSections';
import { declineRequest, reopenRequest } from './order-request.service';

export function OrderRequestDetailPage() {
  return (
    <ResourceDetailPage<OrderRequestRow>
      path={RESOURCE.orderRequests}
      noun="test request"
      title={(row) => row.patient_name}
      subtitle={(row) => `${row.number} · ${ENUM_LABELS.orderRequestStatus[row.status]}`}
      deleteLabel={(row) => `${row.patient_name} (${row.number})`}
      editCapability="orderRequest.edit"
      deleteCapability="orderRequest.delete"
      canEdit={(row) => canEditRequest(row.status)}
      canDelete={(row) => canDeleteRequest(row.status)}
    >
      {(row, refresh) => <Body request={row} onChanged={refresh} />}
    </ResourceDetailPage>
  );
}

interface BodyProps {
  request: OrderRequestRow;
  /** Reloads the request after a transition. */
  onChanged: () => void;
}

function Body({ request, onChanged }: BodyProps) {
  const navigate = useNavigate();
  const transition = useMutation();
  const isBusy = transition.isPending;
  const [decline, setDecline] = useState<DeclineState>({ kind: 'idle' });

  const summary = summariseLines(request.lines);

  async function handleTransition(change: () => Promise<OrderRequestRow>) {
    const outcome = await transition.run(change);
    if (!outcome.ok) return;
    setDecline({ kind: 'idle' });
    onChanged();
  }

  return (
    <div className="c-form-stack">
      {transition.errorMessage && <Banner tone="critical">{transition.errorMessage}</Banner>}

      <RequestStatusBanners request={request} />

      <Card
        title="The request"
        actions={
          hasRequestActions(request.status) ? (
            <RequestActions
              status={request.status}
              isBusy={isBusy}
              canRegister={summary.matchedCount > 0}
              onRegister={() => void navigate(routes.orderRequestRegister(request.id))}
              onStartDecline={() =>
                setDecline((current) =>
                  current.kind === 'declining' ? current : { kind: 'declining', reason: '' },
                )
              }
              onReopen={() => void handleTransition(() => reopenRequest(request.id))}
            />
          ) : undefined
        }
      >
        {decline.kind === 'declining' && (
          <DeclineForm
            reason={decline.reason}
            isBusy={isBusy}
            onReasonChange={(reason) => setDecline({ kind: 'declining', reason })}
            onDecline={() =>
              void handleTransition(() => declineRequest(request.id, { reason: decline.reason }))
            }
          />
        )}

        {summary.matchedCount === 0 && request.status === 'pending' && (
          <Banner tone="warning">
            None of these has been matched to a test yet. Edit the request to match them before
            registering — the registration is what the patient is charged for.
          </Banner>
        )}

        <RequestDetails request={request} />
      </Card>

      <RequestedTestsCard lines={request.lines} summary={summary} />
    </div>
  );
}
